import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMeetingResearch,
  getUpcomingMeetings,
  triggerMeetingResearch,
} from '../lib/meetingsApi';

/**
 * Upcoming "Meet [Nume]" meetings + research status for a user/date window
 * (CLE-234). Cached for 5 minutes, matching the daily summary. Disabled until a
 * concrete user is selected (the business-wide overview has no calendar).
 */
export function useUpcomingMeetings(
  userId: string | undefined,
  date: string | undefined,
  days = 3,
) {
  return useQuery({
    queryKey: ['upcoming-meetings', userId, date, days],
    queryFn: () => getUpcomingMeetings({ userId, date, days }),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Read the full research for one event. When `poll` is true (a job is in
 * flight) the query refetches every few seconds until it leaves the
 * pending/in_progress state, then the caller drops `poll` and the list query is
 * invalidated to refresh the badges.
 */
export function useMeetingResearch(eventId: string | null, poll: boolean) {
  return useQuery({
    queryKey: ['meeting-research', eventId],
    queryFn: () => getMeetingResearch(eventId!),
    enabled: Boolean(eventId),
    refetchInterval: poll ? 3000 : false,
  });
}

/** Trigger research for an event; invalidates the upcoming list on success. */
export function useTriggerMeetingResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => triggerMeetingResearch(eventId),
    onSuccess: (_data, eventId) => {
      void qc.invalidateQueries({ queryKey: ['meeting-research', eventId] });
      void qc.invalidateQueries({ queryKey: ['upcoming-meetings'] });
    },
  });
}
